import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../../services/apiService';
import API_URL from '../../config/api';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

const MAX_RECONNECT_RETRIES = 5;

/**
 * Proveedor de contexto para notificaciones in-app via SSE
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function NotificationProvider({ children }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const eventSourceRef = useRef(null);
  const retriesRef = useRef(0);
  const reconnectTimerRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = apiService.getToken();
      if (!token) return;
      const response = await apiService.authenticatedRequest('/api/notificaciones/no-leidas/count');
      if (response.success) {
        setUnreadCount(response.data.count);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const token = apiService.getToken();
      if (!token) return;
      const response = await apiService.authenticatedRequest('/api/notificaciones?limit=20');
      if (response.success) {
        setNotifications(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  }, []);

  const connectSSE = useCallback(() => {
    const token = apiService.getToken();
    if (!token) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const es = new EventSource(`${API_URL}/api/notificaciones/stream?token=${encodeURIComponent(token)}`);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const notificacion = JSON.parse(event.data);
        setNotifications(prev => [notificacion, ...prev].slice(0, 20));
        setUnreadCount(prev => prev + 1);
        retriesRef.current = 0;
      } catch {
        // ignore parse errors
      }
    };

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;

      retriesRef.current += 1;
      if (retriesRef.current >= MAX_RECONNECT_RETRIES) {
        return;
      }

      // Exponential backoff
      const delay = Math.min(1000 * Math.pow(2, retriesRef.current), 30000);
      reconnectTimerRef.current = setTimeout(connectSSE, delay);
    };
  }, []);

  const disconnectSSE = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    const token = apiService.getToken();
    if (token) {
      fetchUnreadCount();
      fetchNotifications();
      connectSSE();
    }

    const handleAuthChange = () => {
      disconnectSSE();
      const newToken = apiService.getToken();
      if (newToken) {
        retriesRef.current = 0;
        fetchUnreadCount();
        fetchNotifications();
        connectSSE();
      } else {
        setUnreadCount(0);
        setNotifications([]);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        retriesRef.current = 0;
        fetchUnreadCount();
        fetchNotifications();
        if (!eventSourceRef.current) {
          connectSSE();
        }
      }
    };

    window.addEventListener('authChange', handleAuthChange);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      disconnectSSE();
      window.removeEventListener('authChange', handleAuthChange);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [connectSSE, disconnectSSE, fetchUnreadCount, fetchNotifications]);

  const markAsRead = useCallback(async (id) => {
    try {
      await apiService.authenticatedRequest(`/api/notificaciones/${id}/leer`, { method: 'PUT' });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, leida: 1 } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // silently fail
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await apiService.authenticatedRequest('/api/notificaciones/leer-todas', { method: 'PUT' });
      setNotifications(prev => prev.map(n => ({ ...n, leida: 1 })));
      setUnreadCount(0);
    } catch {
      // silently fail
    }
  }, []);

  const value = { unreadCount, notifications, markAsRead, markAllAsRead };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
